import React, { useEffect, createRef, useState, useMemo } from 'react';
import propTypes from 'prop-types';
import { ProductImagesForCarousel } from './ProductImagesForCarousel';
import { ProductThumbnailsForCarousel } from './ProductThumbnailsForCarousel';
import { Flex, Stack, Box } from '@chakra-ui/layout';
import { ProductBadge } from '../product-badge';
import { PhotoSwipe } from '@outdoorly/photoswipe';
import { IconButton } from '@chakra-ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@outdoorly/icons';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Controller, Pagination } from 'swiper';
import SwiperCore from 'swiper/core';
// install Swiper modules
SwiperCore.use([Pagination]);

export const ProductCarouselWithThumbnail = (props) => {
  const { product, focusedVariant, deviceType } = props;
  const buttonRefsArray = useMemo(
    () => Array.from({ length: product.images.length }).map(() => createRef()),
    []
  );
  const [minVisibleThumb, setMinVisibleThumb] = useState(0);
  const [shouldShowRightButton, setShouldShowRightButton] = useState(true);
  const [shouldShowLeftButton, setShouldShowLeftButton] = useState(false);
  const [imageIndex, setImageIndex] = useState(-1);
  const isDesktop = deviceType === 'desktop';
  const [imageSwiper, setSwiper] = useState(null);

  const shouldRenderButtons = product.images.length > 9 && isDesktop;
  const shouldRenderDots =
    product.images.length !== 1 && product.images.length < 28 && !isDesktop;

  const isOpen = imageIndex > -1;
  const closeImage = () => setImageIndex(-1);

  useEffect(() => {
    // If there is a possible variant and a valid swiper
    if (focusedVariant) {
      // Get the first variant image and find its
      // index in the product images.
      const imageIndex =
        product?.images.findIndex(
          (image) => image.id === focusedVariant.images?.[0]?.id
        ) ?? -1;

      // If it exists in the product images (it always should), slide to it.
      if (imageIndex > -1 && imageSwiper) {
        imageSwiper.slideTo(imageIndex);
      }
    }
  }, [product, focusedVariant]);

  const scrollSlide = (activeIndex) => {
    if (isDesktop) {
      const buttonIndex = activeIndex;

      buttonRefsArray[
        buttonIndex - 4 <= 0 ? 0 : buttonIndex - 4
      ]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      checkThumbnailButtons(buttonIndex);
    }
  };

  const checkThumbnailButtons = (slideNumber) => {
    if (shouldRenderButtons) {
      if (slideNumber + 4 >= product.images.length) {
        setShouldShowRightButton(false);
      } else if (!shouldShowRightButton) {
        setShouldShowRightButton(true);
      }
      if (
        slideNumber < product.images.length - 4 &&
        slideNumber !== minVisibleThumb
      ) {
        setMinVisibleThumb(slideNumber);
      } else if (
        slideNumber >= product.images.length - 4 &&
        minVisibleThumb === product.images.length - 4
      ) {
        setMinVisibleThumb(product.images.length - 5);
      } else {
        setMinVisibleThumb(product.images.length - 4);
      }
      if (slideNumber <= 4 && shouldShowLeftButton) {
        setShouldShowLeftButton(false);
      } else if (slideNumber > 4 && !shouldShowLeftButton) {
        setShouldShowLeftButton(true);
      }
    } else {
      setMinVisibleThumb(slideNumber);
    }
  };

  const scrollThumbnails = (direction) => {
    let thumbToSlide;
    if (direction === 'left') {
      thumbToSlide = minVisibleThumb - 1;
    } else {
      if (minVisibleThumb < 4) {
        thumbToSlide = 5 + minVisibleThumb;
      } else {
        thumbToSlide = minVisibleThumb + 1;
      }
    }

    scrollSlide(thumbToSlide);
  };

  const children = product.images.map((image, index) => {
    if (image && image.url)
      return (
        <SwiperSlide key={image.id}>
          <ProductImagesForCarousel
            image={image}
            index={index}
            setImageIndex={setImageIndex}
          />
        </SwiperSlide>
      );
  });

  return (
    <Flex position="relative" direction="column" minW="0">
      {isDesktop && isOpen && (
        <PhotoSwipe
          sources={product?.images.map((image) => image.url)}
          isOpen={isOpen}
          onClose={closeImage}
          initialIndex={imageIndex}
        />
      )}
      <Box position="absolute" zIndex={10} ml={4} mt={6}>
        <Stack align="start">
          {product?.tags.map((tag) => (
            <ProductBadge key={tag.tagName} label={tag.tagName} size="md" />
          ))}
        </Stack>
      </Box>
      <Swiper
        modules={[Pagination, Controller]}
        visibleFrames={1}
        spacingPx={0}
        observeParents
        observer
        resizeObserver
        spaceBetween={0}
        backgroundColor="scheme.bgSecondary"
        borderRadius={isDesktop ? '2xl' : 0}
        overflow="hidden"
        pagination={
          shouldRenderDots
            ? {
                clickable: true,
                dynamicBullets: true,
                renderBullet: (index, className) =>
                  `<div class="${className}"></div>`,
              }
            : false
        }
        containerModifierClass="carousel-with-custom-dots-"
        onSwiper={setSwiper}
        onSlideChange={(swiper) => {
          scrollSlide(swiper.activeIndex);
        }}
        mousewheel={{ forceToAxis: true }}
      >
        {children}
      </Swiper>
      {isDesktop && product?.images?.length !== 1 && (
        <ul className="multi-carousel-dots">
          {product.images.map((image, index) => {
            return (
              image &&
              image.url && (
                <ProductThumbnailsForCarousel
                  image={image}
                  index={index}
                  key={index}
                  buttonRefsArray={buttonRefsArray}
                  isActive={index === imageSwiper?.activeIndex}
                  onClick={() => imageSwiper?.slideTo(index)}
                />
              )
            );
          })}
        </ul>
      )}
      <IconButton
        isRound
        display={shouldRenderButtons && shouldShowLeftButton ? 'flex' : 'none'}
        icon={<ChevronLeftIcon />}
        variant="outline"
        position="absolute"
        size="sm"
        left={0}
        ml="10px"
        padding="0 !important"
        bottom="14px"
        onClick={() => scrollThumbnails('left')}
        transform="translate(0%, -50%)"
        zIndex="10"
        aria-label="Previous Slide"
        boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
        border="none"
        background="scheme.bgPrimary"
        _focus={{ outline: 0 }}
        _hover={{ transform: 'translate(-0%, -50%) scale(1.04)' }}
      />
      <IconButton
        isRound
        display={shouldRenderButtons && shouldShowRightButton ? 'flex' : 'none'}
        icon={<ChevronRightIcon />}
        padding="0 !important"
        variant="outline"
        position="absolute"
        size="sm"
        right={0}
        mr={'15px'}
        bottom="14px"
        onClick={() => scrollThumbnails('right')}
        transform="translate(-0%, -50%)"
        zIndex="10"
        aria-label="Next Slide"
        boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
        border="none"
        background="scheme.bgPrimary"
        _focus={{ outline: 0 }}
        _hover={{ transform: 'translate(-0%, -50%) scale(1.04)' }}
      />
    </Flex>
  );
};

ProductCarouselWithThumbnail.propTypes = {
  product: propTypes.object,
  focusedVariant: propTypes.object,
  deviceType: propTypes.string,
};
